import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, ViewEncapsulation } from '@angular/core'
import { AppUserDto, UserReviewClient } from 'src/app/shared/services/ppv.api'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

export interface DeferralRequestRow {
   id: number
   revieweeId: string
   revieweeName: string
   hospitalName: string
   role: string
   cycle: string
   deferredByName: string
   dateDeferred: Date | string
   hireDate: Date | string
   status: 'Pending' | 'Deferred' | 'Approved' | 'ReEnabled' | 'Rejected'
   deferReason?: string

   isFlagged?: boolean
   saving?: boolean
   flagSaving?: boolean
   messageCount?: number
   unreadCount?: number
   fuOpacity?: string
}

export interface DeferralFollowUpMessage {
   id: number
   deferralId: number
   message: string
   sentById: string
   sentByName: string
   isAdmin: boolean
   sentOn: Date | string
}

interface PrivateAdminMessage {
   id: number
   threadId: number
   sentByUserId: number
   sentByName: string
   message: string
   sentOn: Date | string
   isMine: boolean
}

interface PrivateAdminMessageThread {
   id: number
   contextType?: string
   contextId?: string
   contextLabel?: string
   participantNames?: string[]
   isUnread?: boolean
   messages: PrivateAdminMessage[]
}

type SortColumn = 'revieweeName' | 'hospitalName' | 'cycle' | 'role' | 'hireDate' | 'dateDeferred' | 'status'

@Component({
   selector: 'app-deferral-requests',
   templateUrl: './deferral-requests.component.html',
   styleUrls: ['./deferral-requests.component.scss'],
   standalone: false,
   encapsulation: ViewEncapsulation.None,
})
export class DeferralRequestsComponent implements OnInit, OnChanges {
   @Input() currentAdminName = ''
   @Input() pendingDeferralId: number | null = null
   @Output() deferralCountChange = new EventEmitter<number>()
   @Output() navigateToAuditLog = new EventEmitter<void>()

   allRequests: DeferralRequestRow[] = []
   filteredRequests: DeferralRequestRow[] = []
   loading = false

   get totalDeferred(): number {
      return this.allRequests.filter((r) => r.status === 'Deferred' || r.status === 'Approved').length
   }
   get pendingCount(): number {
      return this.allRequests.filter((r) => r.status === 'Pending').length
   }
   get reEnabledCount(): number {
      return this.allRequests.filter((r) => r.status === 'ReEnabled').length
   }
   get recentDeferralCount(): number {
      // Count requests deferred in the last 24 hours
      const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000)
      return this.allRequests.filter((r) => new Date(r.dateDeferred) >= cutoff).length
   }

   searchQuery = ''
   roleFilter = ''
   statusFilter = ''
   selectedStatusFilters: string[] = ['Pending']
   userHospitalFilter = ''
   hospitalOptions: string[] = []

   public userDeferredOptions: Array<{ label: string; value: string | null }> = [
      { label: 'All statuses', value: '' },
      { label: 'Pending', value: 'Pending' },
      { label: 'Deferred', value: 'Approved' },
      { label: 'Rejected', value: 'Rejected' },
      { label: 'Re-enabled', value: 'ReEnabled' },
   ]

   public userCycleOptions: Array<{ label: string; value: string | null }> = [
      { label: 'All cycles', value: '' },
      { label: 'aDVM Q1', value: 'aDVM Q1' },
      { label: 'aDVM Q2', value: 'aDVM Q2' },
      { label: 'aDVM Q3', value: 'aDVM Q3' },
      { label: 'aDVM Q4', value: 'aDVM Q4' },
      { label: 'HM', value: 'HM' },
      { label: 'COS', value: 'COS' },
      { label: 'RM', value: 'RM' },
      { label: 'DO', value: 'DO' },
      { label: 'OPEN', value: 'OPEN' },
      { label: 'TEAM', value: 'TEAM' },
      /*{ label: 'Clinical', value: 'CLINICAL' },
      { label: 'Cross-Trained', value: 'CROSSTRAINED' },
      { label: 'Reception', value: 'RECEPTION' },*/
   ]

   sortColumn: SortColumn = 'dateDeferred'
   sortDir: 'asc' | 'desc' = 'asc'

   actionSuccess: string | null = null
   actionError: string | null = null

   rejectApproveDialogRequest: DeferralRequestRow | null = null
   rejectReason = ''
   deferDialogType = ''
   moveDeferredToOpenCycle: boolean | null = null

   followUpDialogRequest: DeferralRequestRow | null = null
   followUpMessages: DeferralFollowUpMessage[] = []
   followUpMessage = ''
   followUpLoading = false
   followUpSaving = false
   privateAdminThreads: PrivateAdminMessageThread[] = []
   privateAdminLoading = false

   privateAskOpen = false
   privateUserSearchQuery = ''
   privateUserSearchResults: AppUserDto[] = []
   selectedPrivateUser: AppUserDto | null = null
   showPrivateUserDropdown = false
   privateSearchLoading = false
   privateQuestion = ''
   privateQuestionSending = false
   privateReplyText: Record<number, string> = {}
   privateReplySending: Record<number, boolean> = {}
   private privateSearchDebounce: any = null

   private lastOpenedPendingDeferralId: number | null = null

   private readonly AVATAR_PALETTES = [
      { bg: '#e0eef6', fg: '#0374b6' },
      { bg: '#daf3f2', fg: '#26b5ae' },
      { bg: '#ffefcc', fg: '#976a12' },
      { bg: '#fdebe4', fg: '#f05d44' },
      { bg: '#e9dee9', fg: '#831183' },
      { bg: '#fae8e8', fg: '#d80c47' },
   ]

   deferQuarter: string | null = null

   readonly deferQuarterOptions: Array<{ value: string; label: string; sub: string; endsOn: string }> = [
      { value: 'Q4 2026', label: 'Q4 2026', sub: '(Oct – Dec)', endsOn: '2026-12-31' },
      { value: 'Q1 2027', label: 'Q1 2027', sub: '(Jan – Mar)', endsOn: '2027-03-31' },
      { value: 'Q2 2027', label: 'Q2 2027', sub: '(Apr – Jun)', endsOn: '2027-06-30' },
      { value: 'Q3 2027', label: 'Q3 2027', sub: '(Jul – Sep)', endsOn: '2027-09-30' },
      { value: '1 Year', label: '1 Year', sub: '', endsOn: '' },
   ]

   get isAutoApprovedDeferral(): boolean {
      return !!this.deferQuarter
   }

   selectDeferQuarter(value: string | null): void {
      this.deferQuarter = value
   }

   constructor(private userReviewClient: UserReviewClient) {}

   ngOnInit(): void {
      this.loadDeferralRequests()
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (!changes['pendingDeferralId']) return

      if (!this.pendingDeferralId) {
         this.lastOpenedPendingDeferralId = null
         return
      }

      this.openPendingDeferral()
   }

   loadDeferralRequests(): void {
      this.loading = true
      this.userReviewClient.getDeferralRequests().subscribe({
         next: (data) => {
            this.allRequests = data.map((d) => ({
               id: d.id,
               revieweeId: d.revieweeId,
               revieweeName: d.revieweeName,
               hospitalName: d.hospitalName,
               hireDate: d.hireDate,
               role: d.role,
               cycle: d.cycle,
               deferredByName: d.requestedByName,
               dateDeferred: d.requestedOn,
               status: d.status as DeferralRequestRow['status'],
               deferReason: d.reason,
               isFlagged: !!d.isFlagged,
               saving: false,
               flagSaving: false,
               fuOpacity: '0.5',
            }))
            this.applyFilters()
            this.hospitalOptions = [...new Set(this.filteredRequests.map((u) => u.hospitalName).filter(Boolean))].sort()
            this.emitBadgeCount()
            this.loading = false
            this.loadUnreadCounts()
            this.openPendingDeferral()
         },
         error: () => {
            this.loading = false
            this.actionError = 'Failed to load deferral requests.'
         },
      })
   }

   private loadUnreadCounts(): void {
      const rowMap = new Map(this.allRequests.map((r) => [r.id, r]))
      this.userReviewClient.getDeferralFollowUpCounts().subscribe({
         next: (counts) => {
            counts.forEach((c) => {
               const req = rowMap.get(c.deferralId)
               if (!req) return
               req.messageCount = c.totalCount
               req.unreadCount = c.unreadCount
               req.fuOpacity = req.status !== 'Pending' ? '0.5' : c.totalCount < 1 ? '0.5' : '1'
            })
         },
         error: () => {
            // non-critical — counts just won't show
         },
      })
   }

   getUnreadCount(req: DeferralRequestRow): number {
      return req.unreadCount ?? 0
   }

   applyFilters(): void {
      const q = this.searchQuery.toLowerCase()
      this.filteredRequests = this.allRequests.filter((r) => {
         const matchesSearch = !q || r.revieweeName?.toLowerCase().includes(q) || r.hospitalName?.toLowerCase().includes(q)
         const matchesRole = !this.roleFilter || r.cycle === this.roleFilter
         const matchesStatus = !this.statusFilter || r.status === this.statusFilter
         const matchesHospital = !this.userHospitalFilter || r.hospitalName === this.userHospitalFilter
         const matchesStatusFilters = this.selectedStatusFilters.length === 0 || this.selectedStatusFilters.includes(r.status)

         return matchesSearch && matchesRole && matchesStatus && matchesStatusFilters && matchesHospital
      })

      this.applyFlaggedFirst()
   }

   private applyFlaggedFirst(): void {
      this.filteredRequests = [...this.filteredRequests.filter((r) => r.isFlagged), ...this.filteredRequests.filter((r) => !r.isFlagged)]
   }

   approveRequest(): void {
      if (!this.rejectApproveDialogRequest) return
      const req = this.rejectApproveDialogRequest
      req.saving = true

      this.userReviewClient
         .approveDeferralRequest(req.id, {
            reason: this.rejectReason,
            revieweeId: req.revieweeId,
            whenOpenCompletedBy: this.deferQuarter,
            reviewType: ['Q1', 'Q2', 'Q3', 'Q4'].includes(req.cycle) ? 'DVM' : req.cycle,
         })
         .subscribe({
            next: () => {
               req.status = 'Approved'
               req.saving = false
               this.closeRejectApproveDialog()
               this.applyFilters()
               this.emitBadgeCount()
               this.showSuccess('Deferral request approved.')
            },
            error: () => {
               req.saving = false
               this.showError('Failed to approve request. Please try again.')
            },
         })
   }

   openRejectApproveDialog(req: DeferralRequestRow, type: string): void {
      this.rejectApproveDialogRequest = req
      this.deferDialogType = type
      this.rejectReason = ''
   }

   closeRejectApproveDialog(): void {
      this.rejectApproveDialogRequest = null
      this.deferDialogType = ''
      this.rejectReason = ''
      this.moveDeferredToOpenCycle = null
      this.deferQuarter = ''
   }

   confirmReject(): void {
      if (!this.rejectApproveDialogRequest) return
      const req = this.rejectApproveDialogRequest
      req.saving = true

      this.userReviewClient
         .rejectDeferralRequest(req.id, {
            reason: this.rejectReason,
            revieweeId: req.revieweeId,
            whenOpenCompletedBy: this.deferQuarter,
            reviewType: ['Q1', 'Q2', 'Q3', 'Q4'].includes(req.cycle) ? 'DVM' : req.cycle,
         })
         .subscribe({
            next: () => {
               req.status = 'Rejected'
               req.saving = false
               this.closeRejectApproveDialog()
               this.applyFilters()
               this.emitBadgeCount()
               this.showSuccess('Deferral request rejected.')
            },
            error: () => {
               req.saving = false
               this.showError('Failed to reject request. Please try again.')
            },
         })
   }

   reEnableUser(req: DeferralRequestRow): void {
      req.saving = true
      this.userReviewClient.reEnableDeferredUser(req.revieweeId).subscribe({
         next: () => {
            req.status = 'ReEnabled'
            req.saving = false
            this.applyFilters()
            this.emitBadgeCount()
            this.showSuccess(`${req.revieweeName} has been re-enabled.`)
         },
         error: () => {
            req.saving = false
            this.showError('Failed to re-enable user. Please try again.')
         },
      })
   }

   toggleFlag(req: DeferralRequestRow, event?: Event): void {
      event?.stopPropagation()
      if (req.flagSaving) return

      const next = !req.isFlagged
      req.flagSaving = true

      this.userReviewClient.setDeferralFlag(req.id, next).subscribe({
         next: () => {
            req.isFlagged = next
            req.flagSaving = false
            this.applyFlaggedFirst()
            this.showSuccess(next ? `${req.revieweeName} has been flagged.` : `Flag removed for ${req.revieweeName}.`)
         },
         error: () => {
            req.flagSaving = false
            this.showError('Failed to update the flag. Please try again.')
         },
      })
   }

   onStatusChipChange(event: any): void {
      this.selectedStatusFilters = event.value ?? []
      this.applyFilters()
   }

   private openPendingDeferral(): void {
      if (!this.pendingDeferralId || this.lastOpenedPendingDeferralId === this.pendingDeferralId || !this.allRequests.length) return

      const req = this.allRequests.find((r) => r.id === Number(this.pendingDeferralId))
      if (!req) return

      this.lastOpenedPendingDeferralId = this.pendingDeferralId
      this.openFollowUpDialog(req)
   }

   openFollowUpDialog(req: DeferralRequestRow): void {
      this.followUpDialogRequest = req
      this.followUpMessages = []
      this.privateAdminThreads = []
      this.followUpMessage = ''
      this.resetPrivateQuestionComposer()
      this.privateReplyText = {}
      this.privateReplySending = {}
      this.followUpLoading = true
      this.loadPrivateAdminMessages(req)
      this.userReviewClient.getDeferralFollowUps(req.id).subscribe({
         next: (data) => {
            this.followUpMessages = data.map((d) => ({
               id: d.id,
               deferralId: d.deferralId,
               message: d.message,
               sentById: d.sentById,
               sentByName: d.sentByName,
               isAdmin: d.isAdmin,
               sentOn: d.sentOn,
            }))
            this.followUpLoading = false
         },
         error: () => {
            this.followUpLoading = false
            this.showError('Failed to load follow-up messages.')
         },
      })
   }

   private loadPrivateAdminMessages(req: DeferralRequestRow): void {
      this.privateAdminLoading = true
      this.userReviewClient.getAdminMessages().subscribe({
         next: (data: any[]) => {
            this.privateAdminThreads = (data ?? []).filter((thread) => this.isPrivateDeferralThread(thread, req.id)) as PrivateAdminMessageThread[]
            this.privateAdminLoading = false

            this.privateAdminThreads
               .filter((thread) => thread.isUnread)
               .forEach((thread) => {
                  this.userReviewClient.markAdminMessageRead(thread.id).subscribe({
                     error: () => {},
                  })
               })
         },
         error: () => {
            this.privateAdminThreads = []
            this.privateAdminLoading = false
         },
      })
   }

   private isPrivateDeferralThread(thread: any, deferralId: number): boolean {
      const context = `${thread?.contextType ?? ''} ${thread?.contextLabel ?? ''}`.toLowerCase()
      return context.includes('deferral') && String(thread?.contextId ?? '') === String(deferralId)
   }

   closeFollowUpDialog(): void {
      this.followUpDialogRequest = null
      this.followUpMessages = []
      this.privateAdminThreads = []
      this.privateAdminLoading = false
      this.followUpMessage = ''
      this.resetPrivateQuestionComposer()
      this.privateReplyText = {}
      this.privateReplySending = {}
   }

   openPrivateQuestionComposer(): void {
      this.privateAskOpen = true
      this.privateUserSearchQuery = ''
      this.privateUserSearchResults = []
      this.selectedPrivateUser = null
      this.showPrivateUserDropdown = false
      this.privateQuestion = ''
   }

   closePrivateQuestionComposer(): void {
      this.resetPrivateQuestionComposer()
   }

   onPrivateUserSearchInput(): void {
      const q = this.privateUserSearchQuery.trim()

      if (this.selectedPrivateUser) return

      if (q.length < 2) {
         this.privateUserSearchResults = []
         this.showPrivateUserDropdown = false
         this.privateSearchLoading = false
         clearTimeout(this.privateSearchDebounce)
         return
      }

      clearTimeout(this.privateSearchDebounce)
      this.privateSearchLoading = true
      this.privateSearchDebounce = setTimeout(() => {
         this.userReviewClient.getAppUsers().subscribe({
            next: (users) => {
               this.privateUserSearchResults = (users ?? [])
                  .filter((u) => u.displayName?.toLowerCase().includes(q.toLowerCase()))
                  .filter((u) => u.displayName !== this.currentAdminName)
                  .slice(0, 8)
               this.showPrivateUserDropdown = true
               this.privateSearchLoading = false
            },
            error: () => {
               this.privateSearchLoading = false
               this.showError('Failed to search users.')
            },
         })
      }, 300)
   }

   selectPrivateUser(user: AppUserDto): void {
      this.selectedPrivateUser = user
      this.privateUserSearchQuery = user.displayName
      this.privateUserSearchResults = []
      this.showPrivateUserDropdown = false
      clearTimeout(this.privateSearchDebounce)
   }

   clearPrivateUser(): void {
      this.selectedPrivateUser = null
      this.privateUserSearchQuery = ''
      this.privateUserSearchResults = []
      this.showPrivateUserDropdown = false
   }

   onPrivateSearchBlur(): void {
      setTimeout(() => (this.showPrivateUserDropdown = false), 150)
   }

   sendPrivateQuestion(): void {
      if (!this.followUpDialogRequest || !this.selectedPrivateUser || !this.privateQuestion?.trim() || this.privateQuestionSending) return

      const participantUserId = Number(this.selectedPrivateUser.id)
      if (!participantUserId) {
         this.showError('That user does not have a valid Performance Hub user ID.')
         return
      }

      const req = this.followUpDialogRequest
      this.privateQuestionSending = true

      this.userReviewClient
         .createAdminMessageThread({
            participantUserIds: [participantUserId],
            contextType: 'Deferral',
            contextId: String(req.id),
            contextLabel: `Deferral request: ${req.revieweeName}`,
            contextUrl: `/performance-hub/admin/user-management?deferralId=${req.id}`,
            message: this.privateQuestion.trim(),
         })
         .subscribe({
            next: () => {
               const recipientName = this.selectedPrivateUser?.displayName ?? 'user'
               this.privateQuestionSending = false
               this.resetPrivateQuestionComposer()
               this.loadPrivateAdminMessages(req)
               this.showSuccess(`Private question sent to ${recipientName}.`)
            },
            error: () => {
               this.privateQuestionSending = false
               this.showError('Failed to send the private question. Please try again.')
            },
         })
   }

   sendPrivateReply(thread: PrivateAdminMessageThread): void {
      const message = this.privateReplyText[thread.id]?.trim()
      if (!message || this.privateReplySending[thread.id]) return

      this.privateReplySending[thread.id] = true
      this.userReviewClient.replyToAdminMessage(thread.id, { message }).subscribe({
         next: () => {
            this.privateReplyText[thread.id] = ''
            this.privateReplySending[thread.id] = false
            if (this.followUpDialogRequest) this.loadPrivateAdminMessages(this.followUpDialogRequest)
         },
         error: () => {
            this.privateReplySending[thread.id] = false
            this.showError('Failed to send the private reply. Please try again.')
         },
      })
   }

   getPrivateThreadPeople(thread: PrivateAdminMessageThread): string {
      return thread.participantNames?.length ? thread.participantNames.join(', ') : 'private participant'
   }

   private resetPrivateQuestionComposer(): void {
      this.privateAskOpen = false
      this.privateUserSearchQuery = ''
      this.privateUserSearchResults = []
      this.selectedPrivateUser = null
      this.showPrivateUserDropdown = false
      this.privateSearchLoading = false
      this.privateQuestion = ''
      this.privateQuestionSending = false
      clearTimeout(this.privateSearchDebounce)
   }

   sendFollowUp(): void {
      if (!this.followUpMessage?.trim() || !this.followUpDialogRequest) return
      this.followUpSaving = true
      this.userReviewClient
         .addDeferralFollowUp(this.followUpDialogRequest.id, {
            deferralId: this.followUpDialogRequest.id,
            message: this.followUpMessage.trim(),
            isAdmin: true,
            revieweeId: this.followUpDialogRequest.revieweeId,
         })
         .subscribe({
            next: () => {
               this.followUpMessages.push({
                  id: Date.now(),
                  deferralId: this.followUpDialogRequest!.id,
                  message: this.followUpMessage.trim(),
                  sentById: '',
                  sentByName: 'Admin', //this.currentAdminName,
                  isAdmin: true,
                  sentOn: new Date(),
               })
               this.followUpMessage = ''
               this.followUpSaving = false
            },
            error: () => {
               this.followUpSaving = false
               this.showError('Failed to send message. Please try again.')
            },
         })
   }

   getStatusLabel(status: string): string {
      const map: Record<string, string> = {
         Pending: 'Pending',
         Deferred: 'Deferred',
         Approved: 'Approved',
         ReEnabled: 'Re-enabled',
         Rejected: 'Rejected',
      }
      return map[status] ?? status
   }

   getStatusBadgeClass(status: string): string {
      const map: Record<string, string> = {
         Pending: 'badge-pending',
         Deferred: 'badge-deferred',
         Approved: 'badge-approved',
         ReEnabled: 'badge-reenabled',
         Rejected: 'badge-rejected',
      }
      return map[status] ?? ''
   }
   getUserCycleDisplay(u: any): string {
      if (u.cycle == 'CLINICAL') return 'TEAM'
      if (u.cycle == 'CROSSTRAINED') return 'TEAM'
      if (u.cycle == 'RECEPTION') return 'TEAM'

      if (['Q1', 'Q2', 'Q3', 'Q4'].includes(u.cycle)) {
         return `aDVM ${u.cycle}`
      } else if (u.cycle) {
         return `${u.cycle}`
      }

      switch (u.role) {
         case 'VET-COS':
            return 'COS'
         case 'HM':
            return 'HM'
         case 'OPS-RM':
         case 'AREA_MANAGER':
            return 'RM'
         case 'CORP-DIR':
            return 'DO'
         default:
            return ''
      }
   }

   setUserSort(col: SortColumn): void {
      if (this.sortColumn === col) {
         this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
         this.sortColumn = col
         this.sortDir = 'asc'
      }
      this.applySort()
   }

   private applySort(): void {
      this.filteredRequests = [...this.filteredRequests].sort((a, b) => {
         let aVal = '',
            bVal = ''
         switch (this.sortColumn) {
            case 'revieweeName':
               aVal = a.revieweeName || ''
               bVal = b.revieweeName || ''
               break
            case 'hospitalName':
               aVal = a.hospitalName || ''
               bVal = b.hospitalName || ''
               break
            case 'cycle':
               aVal = this.getUserCycleDisplay(a)
               bVal = this.getUserCycleDisplay(b)
               break
            case 'role':
               aVal = a.role || ''
               bVal = b.role || ''
               break
            case 'hireDate':
               aVal = a.hireDate ? new Date(a.hireDate).toISOString() : ''
               bVal = b.hireDate ? new Date(b.hireDate).toISOString() : ''
               break
            case 'dateDeferred':
               aVal = a.dateDeferred ? new Date(a.dateDeferred).toISOString() : ''
               bVal = b.dateDeferred ? new Date(b.dateDeferred).toISOString() : ''
               break
            case 'status':
               aVal = a.status || ''
               bVal = b.status || ''
               break
         }
         const cmp = aVal.localeCompare(bVal)
         return this.sortDir === 'asc' ? cmp : -cmp
      })

      this.applyFlaggedFirst()
   }

   getInitials(name: string): string {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase()
   }

   getAvatarBg(name: string): string {
      return this.AVATAR_PALETTES[this.hashName(name) % this.AVATAR_PALETTES.length].bg
   }

   getAvatarColor(name: string): string {
      return this.AVATAR_PALETTES[this.hashName(name) % this.AVATAR_PALETTES.length].fg
   }

   private hashName(name: string): number {
      if (!name) return 0
      return name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
   }

   private emitBadgeCount(): void {
      this.deferralCountChange.emit(this.pendingCount)
   }

   private showSuccess(msg: string): void {
      this.actionSuccess = msg
      this.actionError = null
      setTimeout(() => (this.actionSuccess = null), 3000)
   }

   private showError(msg: string): void {
      this.actionError = msg
      setTimeout(() => (this.actionError = null), 5000)
   }

   trackByReqId(index: number, req: DeferralRequestRow): number {
      return req.id
   }

   formatDate(date: string): string {
      return dayjs.utc(date).local().format('MMM DD, YYYY\nh:mm A')
   }
}
